import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { BusinessContext, type BusinessContextValue } from '@/contexts/BusinessContext';
import { useDataStore } from '@/hooks/useDataStore';
import { useSettings } from '@/hooks/useSettings';
import { resolveActiveBusiness } from '@/services/activeBusiness.service';
import { seedCatalog } from '@/services/seed.service';
import { getTemplate } from '@/templates';
import type { BusinessProfile, CreateInput, ID, UpdateInput } from '@/types';

export function BusinessProvider({ children }: PropsWithChildren) {
  const store = useDataStore();
  const { settings, update: updateSettings } = useSettings();
  const [businesses, setBusinesses] = useState<BusinessProfile[]>([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void store.businesses.list().then((loaded) => {
      if (cancelled) return;
      setBusinesses(loaded);
      setIsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [store]);

  // `settings.activeBusinessId` is the one record of which business is active;
  // everything business-scoped — customers, quotes, drafts, catalog, the PDF
  // design — follows this value.
  const resolution = useMemo(
    () => resolveActiveBusiness(businesses, settings.activeBusinessId),
    [businesses, settings.activeBusinessId],
  );
  const activeBusiness = resolution.business;

  // A stored id that names no business here is replaced by the one the app
  // fell back to, so it resolves the same way next time. Never destructive:
  // only the pointer is rewritten, no business is touched.
  useEffect(() => {
    if (!isReady || !resolution.needsRepair || !resolution.business) return;
    void updateSettings({ activeBusinessId: resolution.business.id });
  }, [isReady, resolution, updateSettings]);

  const createBusiness = useCallback(
    async (input: CreateInput<BusinessProfile>) => {
      const created = await store.businesses.create(input);
      // Seed before activating, so the catalog is populated the instant the
      // service provider reacts to the new active business.
      await seedCatalog(store, created.id, getTemplate(created.templateId));
      setBusinesses((current) => [...current, created]);
      await updateSettings({ activeBusinessId: created.id, onboardedAt: created.createdAt });
      return created;
    },
    [store, updateSettings],
  );

  const updateBusiness = useCallback(
    async (id: ID, patch: UpdateInput<BusinessProfile>) => {
      const next = await store.businesses.update(id, patch);
      setBusinesses((current) => current.map((item) => (item.id === id ? next : item)));
      return next;
    },
    [store],
  );

  const setActiveBusiness = useCallback(
    async (id: ID) => {
      // Only a business that exists on this device can become active.
      if (!businesses.some((business) => business.id === id)) return;
      await updateSettings({ activeBusinessId: id });
    },
    [businesses, updateSettings],
  );

  // Owners with unsaved edits. A ref, not state: asking must not re-render anyone.
  const unsaved = useRef(new Set<string>());
  const setUnsavedChanges = useCallback((owner: string, dirty: boolean) => {
    if (dirty) unsaved.current.add(owner);
    else unsaved.current.delete(owner);
  }, []);
  const hasUnsavedChanges = useCallback(() => unsaved.current.size > 0, []);

  const value = useMemo<BusinessContextValue>(
    () => ({
      businesses,
      activeBusiness,
      template: activeBusiness ? getTemplate(activeBusiness.templateId) : null,
      isReady,
      createBusiness,
      updateBusiness,
      setActiveBusiness,
      setUnsavedChanges,
      hasUnsavedChanges,
    }),
    [
      businesses, activeBusiness, isReady, createBusiness, updateBusiness, setActiveBusiness,
      setUnsavedChanges, hasUnsavedChanges,
    ],
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}
